import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Search, FileText, ShoppingBag, BookOpen, Sparkles, Compass } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  searchSite,
  highlightSegments,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  type SearchResult,
  type SearchCategory,
} from "@/lib/siteSearch";

interface SiteSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CATEGORY_ICONS: Record<SearchCategory, typeof FileText> = {
  articles: FileText,
  products: ShoppingBag,
  books: BookOpen,
  activities: Sparkles,
  pages: Compass,
};

type Filter = SearchCategory | "all";

const Highlighted = ({ text, term }: { text: string; term: string }) => (
  <>
    {highlightSegments(text, term).map((seg, i) =>
      seg.match ? (
        <mark key={i} className="bg-primary/20 text-primary rounded-sm px-0.5">
          {seg.text}
        </mark>
      ) : (
        <span key={i}>{seg.text}</span>
      )
    )}
  </>
);

const SiteSearchDialog = ({ open, onOpenChange }: SiteSearchDialogProps) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<Filter>(() => {
    if (typeof window === "undefined") return "all";
    return (sessionStorage.getItem("siteSearchFilter") as Filter) || "all";
  });

  // Persist the active category filter across navigations / dialog reopens.
  const updateFilter = useCallback((f: Filter) => {
    setFilter(f);
    try {
      sessionStorage.setItem("siteSearchFilter", f);
    } catch {
      /* ignore storage errors */
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setDebounced("");
      setResults([]);
      // Keep the chosen filter so it persists the next time search opens.
    }
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    if (debounced.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    searchSite(debounced).then((res) => {
      if (cancelled) return;
      setResults(res);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  const handleSelect = useCallback(
    (href: string) => {
      onOpenChange(false);
      if (href.startsWith("/")) navigate(href);
    },
    [navigate, onOpenChange]
  );

  // Which category filters actually have results
  const availableFilters = useMemo<Filter[]>(() => {
    const present = CATEGORY_ORDER.filter((c) =>
      results.some((r) => r.category === c)
    );
    return ["all", ...present];
  }, [results]);

  // If the persisted filter has no results for this query, fall back to "all"
  // so the user never sees an unexplained empty list.
  const effectiveFilter = useMemo<Filter>(
    () => (availableFilters.includes(filter) ? filter : "all"),
    [availableFilters, filter]
  );

  const visible = useMemo(
    () =>
      effectiveFilter === "all"
        ? results
        : results.filter((r) => r.category === effectiveFilter),
    [results, effectiveFilter]
  );

  const grouped = useMemo(() => {
    const map = new Map<SearchCategory, SearchResult[]>();
    for (const r of visible) {
      if (!map.has(r.category)) map.set(r.category, []);
      map.get(r.category)!.push(r);
    }
    return CATEGORY_ORDER.filter((c) => map.has(c)).map((c) => ({
      category: c,
      items: map.get(c)!,
    }));
  }, [visible]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} shouldFilter={false}>
      <CommandInput
        placeholder="Search products, articles, books, activities…"
        value={query}
        onValueChange={setQuery}
      />

      {results.length > 0 && availableFilters.length > 1 && (
        <div className="flex flex-wrap gap-1.5 border-b px-3 py-2">
          {availableFilters.map((f) => {
            const active = effectiveFilter === f;
            return (
              <button
                key={f}
                type="button"
                onClick={() => updateFilter(f)}
                className={`rounded-full px-3 py-1 text-xs font-sans transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-foreground/70 hover:text-foreground"
                }`}
              >
                {CATEGORY_LABELS[f]}
              </button>
            );
          })}
        </div>
      )}

      <CommandList>
        {loading && (
          <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Searching…
          </div>
        )}
        {!loading && debounced.length >= 2 && results.length === 0 && (
          <CommandEmpty>No results for “{debounced}”.</CommandEmpty>
        )}
        {!loading && debounced.length < 2 && (
          <div className="flex flex-col items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Search className="w-5 h-5 opacity-60" />
            Type at least 2 characters to search.
          </div>
        )}
        {grouped.map(({ category, items }) => {
          const Icon = CATEGORY_ICONS[category];
          return (
            <CommandGroup key={category} heading={CATEGORY_LABELS[category]}>
              {items.map((r) => (
                <CommandItem
                  key={r.id}
                  value={r.id}
                  onSelect={() => handleSelect(r.href)}
                  className="flex items-start gap-3 cursor-pointer"
                >
                  {r.image ? (
                    <img
                      src={r.image}
                      alt=""
                      className="w-9 h-9 rounded object-cover shrink-0"
                      loading="lazy"
                    />
                  ) : (
                    <span className="flex w-9 h-9 items-center justify-center rounded bg-secondary shrink-0">
                      <Icon className="w-4 h-4 text-primary" />
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">
                      <Highlighted text={r.title} term={debounced} />
                    </p>
                    {r.description && (
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        <Highlighted text={r.description} term={debounced} />
                      </p>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
    </CommandDialog>
  );
};

export default SiteSearchDialog;
